import { getConfig } from '../../utils/config';

export function getApiUrl(): string {
  return getConfig<string>({
    configName: 'api.SERVER_ENDPOINT',
    defaultValue: 'http://localhost:8080',
  });
}

const SESSION_TOKEN_STORAGE_KEY = 'nice_sap_session_token';

let sessionToken: string | null = null;

function readStoredSessionToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

if (typeof window !== 'undefined') {
  sessionToken = readStoredSessionToken();
}

export function setSessionToken(token: string | null): void {
  sessionToken = token;
  try {
    if (token) {
      sessionStorage.setItem(SESSION_TOKEN_STORAGE_KEY, token);
    } else {
      sessionStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    }
  } catch {
    // sessionStorage may be unavailable in some embedded contexts
  }
}

export function getSessionToken(): string | null {
  return sessionToken;
}

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export async function fetchWithCredentials<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const apiUrl = getApiUrl();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (sessionToken) {
    headers.Authorization = `Bearer ${sessionToken}`;
  }

  const response = await fetch(`${apiUrl}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ description: 'Unknown error' }));
    throw new ApiError(
      error.description || error.error || error.message || `Request failed: ${response.status}`,
      response.status,
      error.code || error.title
    );
  }

  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text);
}
